import { StringDevOverlayModule, OverlayBaseMetrics } from "../core/StringDevOverlayModule";
import { resolveDevtoolsIcon } from "../core/StringDevIconRegistry";
import { makeBadge } from "../core/StringDevElements";
import { bindOutsideClick } from "../core/bindOutsideClick";
import { StringObject } from "../../../objects/StringObject";
import { StringContext } from "../../../core/StringContext";
import { EasingFunctionOutput } from "../../../tools/EasingFunctionTool";
import { styleTxn } from "../../../utils/style-txn";
import { STRING_DEV_PROGRESS_OVERRIDE_KEY } from "../../scroll/StringProgress";
import { PROGRESS_CSS } from "./StringDevProgress.css";

interface ProgressEntry {
  object: StringObject;
  hud: HTMLDivElement;
  badge: HTMLButtonElement;
  onBadgeClick: (event: MouseEvent) => void;
}

interface ProgressMeasurement {
  visible: boolean;
  docX: number;
  docY: number;
  targetId: string;
  progress: number;
  rawProgress: number;
  startPosition: number;
  endPosition: number;
  currentScroll: number;
  key: string;
}

function clampProgress(raw: number): number {
  if (!Number.isFinite(raw)) return 0;
  if (raw <= 0) return 0;
  if (raw >= 1) return 1;
  const epsilon = 1e-4;
  if (raw > 1 - epsilon) return 1;
  if (raw < epsilon) return 0;
  return raw;
}

function formatPercent(value: number): string {
  const percent = clampProgress(value) * 100;
  return `${Math.round(percent * 10) / 10}%`;
}

export class StringDevProgress extends StringDevOverlayModule<ProgressEntry, ProgressMeasurement> {
  static override devtool = {
    id: "progress",
    label: "Progress",
    icon: "progress",
    order: 35,
    group: 2,
    hotkey: { key: "P", shiftKey: true },
    styles: PROGRESS_CSS,
    overlay: {
      layerName: "devtools-progress",
      zIndex: 10012,
      layerAttribute: "data-string-progress-layer",
      overlayId: "progress",
    },
    connects: { keys: ["progress"] },
  };

  private openTargetLabel: string | null = null;
  private panelRoot: HTMLDivElement | null = null;
  private panelTitle: HTMLSpanElement | null = null;
  private panelSlider: HTMLInputElement | null = null;
  private panelValue: HTMLInputElement | null = null;
  private panelEasedValue: HTMLSpanElement | null = null;
  private panelPlayForwardButton: HTMLButtonElement | null = null;
  private panelPlayBackwardButton: HTMLButtonElement | null = null;
  private autoplayRaf: number | null = null;
  private autoplayDirection: 1 | -1 = 1;
  private autoplayProgress = 0;
  private readonly onPanelSliderInputBind = () => {
    this.onPanelSliderInput();
  };
  private unbindOutsideClick: (() => void) | null = null;

  constructor(context: StringContext) {
    super(context);
    this.attributesToMap = [
      ...this.attributesToMap,
      { key: "easing", type: "easing", fallback: this.settings["easing"] },
    ];
  }

  onInit(): void {
    super.onInit();
    this.ensurePanel();
  }

  destroy(): void {
    this.unbindOutsideClick?.();
    this.unbindOutsideClick = null;
    this.panelSlider?.removeEventListener("input", this.onPanelSliderInputBind);
    this.stopAutoplay();
    this.panelRoot?.remove();
    this.panelRoot = null;
    this.panelTitle = null;
    this.panelSlider = null;
    this.panelValue = null;
    this.panelEasedValue = null;
    this.panelPlayForwardButton = null;
    this.panelPlayBackwardButton = null;
    this.clearAllProgressOverrides();
    this.openTargetLabel = null;
    super.destroy();
  }

  protected createOverlayEntry(object: StringObject): ProgressEntry {
    const targetLabel = this.resolveOverlayTargetId(object);
    const hud = makeBadge(targetLabel);
    hud.setAttribute("data-string-devtools-overlay-hud", "");

    const badge = this.createOverlayBadge({
      targetId: targetLabel,
      selectorAttribute: "data-string-progress-badge",
      attributes: {
        "data-stdg-button-toggle": "",
        "data-active": "false",
        "aria-label": `Open progress controller for ${targetLabel}`,
      },
    });
    this.applyOverlayBadgeState(badge, {
      html: resolveDevtoolsIcon(16, "play-r"),
    });

    const onBadgeClick = (event: MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      if (this.openTargetLabel === targetLabel) {
        this.closePanel(targetLabel);
        return;
      }
      this.openPanel(targetLabel);
    };

    badge.addEventListener("click", onBadgeClick);
    hud.appendChild(badge);
    this.badgeLayer.getWorldElement(this.getWorldHost()).appendChild(hud);
    this.applyEntryEnabledState(hud);

    return {
      object,
      hud,
      badge,
      onBadgeClick,
    };
  }

  protected destroyOverlayEntry(entry: ProgressEntry): void {
    const targetLabel = this.resolveOverlayTargetId(entry.object);
    if (this.openTargetLabel === targetLabel) {
      this.clearEntryProgressOverride(entry.object);
      this.closePanel(targetLabel);
    }

    entry.badge.removeEventListener("click", entry.onBadgeClick);
    entry.hud.remove();
  }

  protected measureEntry(entry: ProgressEntry, metrics: OverlayBaseMetrics): ProgressMeasurement {
    const object = entry.object;
    const targetId = this.resolveOverlayTargetId(object);
    const currentScroll = this.data.scroll.transformedCurrent;
    const key = object.getProperty<string>("key") || "--progress";

    const { startPosition, differencePosition } = this.getEntryRange(object);
    const endPosition =
      object.getProperty<number>("end-position") ?? startPosition + differencePosition;

    const rawProgress = this.resolveEntryRawProgress(object, currentScroll);
    const progress = this.resolveEasedProgress(object, rawProgress);

    return {
      visible: metrics.visible,
      docX: metrics.visible ? metrics.baseAnchorX : 0,
      docY: metrics.visible ? metrics.baseAnchorY + metrics.collisionOffset : 0,
      targetId,
      progress,
      rawProgress,
      startPosition,
      endPosition,
      currentScroll,
      key,
    };
  }

  protected applyMeasurement(
    entry: ProgressEntry,
    measurement: ProgressMeasurement | undefined,
  ): void {
    const targetId = this.resolveOverlayTargetId(entry.object);

    if (!measurement || !measurement.visible) {
      entry.hud.setAttribute("data-visible", "false");
      if (this.openTargetLabel === targetId) {
        this.renderPanel();
      }
      return;
    }

    entry.hud.setAttribute("data-visible", "true");
    styleTxn.setProps(entry.hud, {
      transform: `translate3d(${Math.round(measurement.docX)}px, ${Math.round(measurement.docY)}px, 0)`,
    });

    entry.badge.title = `${this.getOverlayTargetLabel(entry.object)}: ${formatPercent(measurement.progress)}`;
    this.applyOverlayBadgeState(entry.badge, {
      label: measurement.progress.toFixed(2),
    });

    if (this.openTargetLabel === measurement.targetId) {
      this.renderPanel();
    }
  }

  protected onEnabledChange(enabled: boolean): void {
    for (const entry of this.entries.values()) {
      this.applyEntryEnabledState(entry.hud);
    }

    if (!enabled) {
      this.closeAllPanels();
      this.clearAllProgressOverrides();
    }

    if (this.panelRoot) {
      this.panelRoot.style.display = enabled ? "" : "none";
    }
  }

  private applyEntryEnabledState(hud: HTMLDivElement): void {
    hud.style.display = this.enabled ? "" : "none";
  }

  private openPanel(targetLabel: string): void {
    this.ensurePanel();
    this.closeAllPanels();
    this.openTargetLabel = targetLabel;
    const entry = this.findEntryByTargetLabel(targetLabel);
    if (entry) entry.badge.setAttribute("data-active", "true");
    if (this.panelRoot) {
      this.unbindOutsideClick = bindOutsideClick(this.panelRoot, () => this.closeAllPanels(), {
        ignore: this.collectEntryHuds(),
      });
    }
    this.renderPanel();
  }

  private closePanel(targetLabel: string): void {
    this.stopAutoplay();
    const entry = this.findEntryByTargetLabel(targetLabel);
    if (entry) {
      entry.badge.setAttribute("data-active", "false");
      this.clearEntryProgressOverride(entry.object);
    }

    if (this.openTargetLabel === targetLabel) {
      this.openTargetLabel = null;
    }

    this.renderPanel();
  }

  private closeAllPanels(): void {
    this.stopAutoplay();
    for (const entry of this.entries.values()) {
      entry.badge.setAttribute("data-active", "false");
      this.clearEntryProgressOverride(entry.object);
    }
    this.openTargetLabel = null;
    this.unbindOutsideClick?.();
    this.unbindOutsideClick = null;
    this.renderPanel();
  }

  private collectEntryHuds(): HTMLElement[] {
    const huds: HTMLElement[] = [];
    for (const entry of this.entries.values()) {
      huds.push(entry.hud);
    }
    return huds;
  }

  private startAutoplay(direction: 1 | -1): void {
    this.stopAutoplay();
    this.autoplayDirection = direction;
    this.autoplayProgress = direction === 1 ? 0 : 1;

    const duration = 1200;
    let lastTime: number | null = null;

    const tick = (time: number) => {
      if (lastTime === null) {
        lastTime = time;
        this.autoplayRaf = requestAnimationFrame(tick);
        return;
      }

      const delta = (time - lastTime) / duration;
      lastTime = time;
      this.autoplayProgress += delta * this.autoplayDirection;

      const entry = this.openTargetLabel ? this.findEntryByTargetLabel(this.openTargetLabel) : null;

      if (this.autoplayProgress >= 1) {
        this.autoplayProgress = 1;
        if (entry) this.setEntryProgressOverride(entry.object, 1);
        this.stopAutoplay();
        return;
      }

      if (this.autoplayProgress <= 0) {
        this.autoplayProgress = 0;
        if (entry) this.setEntryProgressOverride(entry.object, 0);
        this.stopAutoplay();
        return;
      }

      if (entry) {
        this.setEntryProgressOverride(entry.object, this.autoplayProgress);
      }

      this.autoplayRaf = requestAnimationFrame(tick);
    };

    this.autoplayRaf = requestAnimationFrame(tick);
    this.syncPlayButtonState();
  }

  private stopAutoplay(): void {
    if (this.autoplayRaf !== null) {
      cancelAnimationFrame(this.autoplayRaf);
      this.autoplayRaf = null;
    }
    this.syncPlayButtonState();
  }

  private syncPlayButtonState(): void {
    const isPlaying = this.autoplayRaf !== null;
    const isForward = isPlaying && this.autoplayDirection === 1;
    const isBackward = isPlaying && this.autoplayDirection === -1;
    this.panelPlayForwardButton?.setAttribute("data-active", String(isForward));
    this.panelPlayBackwardButton?.setAttribute("data-active", String(isBackward));
  }

  private getEntryRange(object: StringObject): {
    startPosition: number;
    differencePosition: number;
  } {
    const startPosition =
      object.getProperty<number>("start-position") ??
      object.getProperty<number>("progress-start-position") ??
      0;
    const differencePosition =
      object.getProperty<number>("difference-position") ??
      object.getProperty<number>("progress-difference-position") ??
      0;
    return { startPosition, differencePosition };
  }

  private resolveEntryRawProgress(object: StringObject, currentScroll: number): number {
    const overrideRawProgress = object.getProperty<number | null>(STRING_DEV_PROGRESS_OVERRIDE_KEY);
    if (overrideRawProgress != null) {
      return clampProgress(overrideRawProgress);
    }

    const { startPosition, differencePosition } = this.getEntryRange(object);

    return clampProgress(
      differencePosition === 0 ? 0 : (currentScroll - startPosition) / differencePosition,
    );
  }

  private resolveEasedProgress(object: StringObject, rawProgress: number): number {
    const easing = object.getProperty<EasingFunctionOutput>("easing");
    return typeof easing === "function" ? clampProgress(easing(rawProgress)) : rawProgress;
  }

  private setEntryProgressOverride(object: StringObject, rawProgress: number): void {
    object.setProperty(STRING_DEV_PROGRESS_OVERRIDE_KEY, clampProgress(rawProgress));
    this.applyObjectProgress(object, rawProgress);
    const entry = this.entries.get(object.id);
    if (entry) this.scheduleSingleSync(entry);
    else this.scheduleSync();
  }

  private clearEntryProgressOverride(object: StringObject): void {
    if (object.getProperty<number | null>(STRING_DEV_PROGRESS_OVERRIDE_KEY) == null) {
      return;
    }

    object.setProperty<number | null>(STRING_DEV_PROGRESS_OVERRIDE_KEY, null);
    const liveRawProgress = this.resolveEntryRawProgress(
      object,
      this.data.scroll.transformedCurrent,
    );
    this.applyObjectProgress(object, liveRawProgress);
    const entry = this.entries.get(object.id);
    if (entry) this.scheduleSingleSync(entry);
    else this.scheduleSync();
  }

  private clearAllProgressOverrides(): void {
    for (const entry of this.entries.values()) {
      this.clearEntryProgressOverride(entry.object);
    }
  }

  private applyObjectProgress(object: StringObject, rawProgress: number): void {
    const normalizedRawProgress = clampProgress(rawProgress);
    const easing = object.getProperty<EasingFunctionOutput>("easing");
    const progress = this.resolveEasedProgress(object, normalizedRawProgress);
    const key = object.getProperty<string>("key");

    object.setProperty("progress-raw", normalizedRawProgress);
    object.setProperty("progress-value", progress);
    object.setProperty<number>("progress-applied", progress);

    const eventName =
      object.getProperty<string>("event-progress-name") ??
      object.getScopedEventName("object:progress");
    object.setProperty("event-progress-name", eventName);
    if (eventName) {
      this.events.emit(eventName, progress);
    }

    this.tools.styleTxn.run(() => {
      if (key) {
        this.tools.styleTxn.setVar(object.htmlElement, key, progress);
      }

      for (const mirror of object.mirrorObjects) {
        const mirrorProgress = mirror.applyProgress(
          normalizedRawProgress,
          typeof easing === "function" ? easing : undefined,
        );
        mirror.setProperty("progress", mirrorProgress);
        if (key) {
          this.tools.styleTxn.setVar(mirror.htmlElement, key, mirrorProgress);
        }
      }
    });
  }

  private ensurePanel(): void {
    if (this.panelRoot) {
      return;
    }

    const panel = document.createElement("div");
    panel.setAttribute("data-string-progress-panel", "");
    panel.setAttribute("data-stdg", "");
    panel.setAttribute("data-stdg-panel", "");
    panel.setAttribute("data-stdg-progress", "");
    panel.setAttribute("data-string-devtools-theme", "");
    panel.setAttribute("data-open", "false");
    panel.setAttribute("data-target-id", "");
    panel.setAttribute("hidden", "");
    panel.addEventListener("pointerdown", (event) => event.stopPropagation());

    panel.innerHTML = `
      <div data-stdg-panel-header>
        <span data-string-progress-dock-title>Element ID</span>
        <nav data-stdg-button-left data-string-progress-dock-play-group>
          <button
            type="button"
            data-stdg-button
            data-stdg-button-toggle
            data-stdg-button-icon-16
            data-string-progress-dock-play-backward
            aria-label="Play backward 100→0"
            title="Play backward 100→0"
            data-active="false"
          >
            ${resolveDevtoolsIcon(16, "play-l")}
          </button>
          <button
            type="button"
            data-stdg-button
            data-stdg-button-toggle
            data-stdg-button-icon-16
            data-string-progress-dock-play-forward
            aria-label="Play forward 0→100"
            title="Play forward 0→100"
            data-active="false"
          >
            ${resolveDevtoolsIcon(16, "play-r")}
          </button>
        </nav>
        <button
          type="button"
          data-stdg-button
          data-stdg-button-right
          data-stdg-button-icon-16
          data-string-progress-dock-close
          aria-label="Close progress controller"
          title="Close progress controller"
        >
          ${resolveDevtoolsIcon(16, "close")}
        </button>
      </div>
      <div data-stdg-panel-content>
        <div data-stdg-panel-field-slider-row>
          <div
            data-stdg-panel-field
            data-stdg-panel-field-slider
            data-string-progress-field
          >
            <label for="string-dev-progress-slider">Progress</label>
            <input
              id="string-dev-progress-slider"
              type="range"
              min="0"
              max="1000"
              step="1"
              value="0"
              aria-label="Preview progress"
              data-stdg-input
              data-string-progress-slider
              style="--string-progress-slider-fill: 0%;"
            />
          </div>
          <div data-stdg-panel-field data-string-progress-value-card>
            <input
              id="string-dev-progress-value"
              type="number"
              min="0"
              max="100"
              step="1"
              inputmode="numeric"
              value="0"
              aria-label="Progress value"
              data-stdg-input
              data-string-progress-value-number
            />
            <label
              for="string-dev-progress-value"
              data-stdg-field-input-disable
              data-string-progress-value-unit
            >%</label>
          </div>
        </div>
        <span data-field="eased" hidden>0%</span>
      </div>
    `;

    this.panelRoot = panel;
    this.panelTitle = panel.querySelector("[data-string-progress-dock-title]") as HTMLSpanElement;
    this.panelSlider = panel.querySelector("[data-string-progress-slider]") as HTMLInputElement;
    this.panelValue = panel.querySelector(
      "[data-string-progress-value-number]",
    ) as HTMLInputElement;
    this.panelEasedValue = panel.querySelector('[data-field="eased"]') as HTMLSpanElement;

    this.panelPlayForwardButton = panel.querySelector(
      "[data-string-progress-dock-play-forward]",
    ) as HTMLButtonElement;
    this.panelPlayForwardButton.addEventListener("click", (event) => {
      event.stopPropagation();
      if (this.autoplayRaf !== null && this.autoplayDirection === 1) {
        this.stopAutoplay();
      } else {
        this.startAutoplay(1);
      }
    });

    this.panelPlayBackwardButton = panel.querySelector(
      "[data-string-progress-dock-play-backward]",
    ) as HTMLButtonElement;
    this.panelPlayBackwardButton.addEventListener("click", (event) => {
      event.stopPropagation();
      if (this.autoplayRaf !== null && this.autoplayDirection === -1) {
        this.stopAutoplay();
      } else {
        this.startAutoplay(-1);
      }
    });

    const closeButton = panel.querySelector(
      "[data-string-progress-dock-close]",
    ) as HTMLButtonElement;
    closeButton.addEventListener("click", (event) => {
      event.stopPropagation();
      if (this.openTargetLabel) {
        this.closePanel(this.openTargetLabel);
      }
    });

    this.panelSlider.addEventListener("input", this.onPanelSliderInputBind);
    this.panelValue.addEventListener("input", () => {
      if (!this.openTargetLabel || !this.panelValue) return;
      const entry = this.findEntryByTargetLabel(this.openTargetLabel);
      if (!entry) return;
      const percent = Math.max(0, Math.min(100, Number(this.panelValue.value)));
      if (!Number.isFinite(percent)) return;
      this.stopAutoplay();
      const rawProgress = percent / 100;
      this.autoplayProgress = rawProgress;
      this.setEntryProgressOverride(entry.object, rawProgress);
    });
    this.panelValue.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        (event.target as HTMLInputElement).blur();
      }
    });

    this.hudLayer.getWorldElement(this.getWorldHost()).appendChild(panel);
  }

  private onPanelSliderInput(): void {
    if (!this.openTargetLabel || !this.panelSlider) {
      return;
    }

    const entry = this.findEntryByTargetLabel(this.openTargetLabel);
    if (!entry) {
      return;
    }

    this.stopAutoplay();
    const rawProgress = clampProgress(Number(this.panelSlider.value) / 1000);
    this.autoplayProgress = rawProgress;
    this.setEntryProgressOverride(entry.object, rawProgress);
  }

  private renderPanel(): void {
    if (
      !this.panelRoot ||
      !this.panelTitle ||
      !this.panelSlider ||
      !this.panelValue ||
      !this.panelEasedValue
    ) {
      return;
    }

    const activeEntry = this.openTargetLabel
      ? this.findEntryByTargetLabel(this.openTargetLabel)
      : null;
    if (!this.enabled || !activeEntry) {
      this.panelRoot.setAttribute("hidden", "");
      this.panelRoot.setAttribute("data-open", "false");
      this.panelRoot.setAttribute("data-target-id", "");
      return;
    }

    const targetId = this.resolveOverlayTargetId(activeEntry.object);
    const targetLabel = this.getOverlayTargetLabel(activeEntry.object);
    const measurement = this.measurements.get(activeEntry.object.id);
    const progress = measurement ? clampProgress(measurement.progress) : 0;
    const rawProgress = measurement ? clampProgress(measurement.rawProgress) : 0;

    this.panelRoot.removeAttribute("hidden");
    this.panelRoot.setAttribute("data-open", "true");
    this.panelRoot.setAttribute("data-target-id", targetId);
    this.panelTitle.textContent = targetLabel;
    this.panelSlider.setAttribute("aria-label", `Preview progress for ${targetLabel}`);

    styleTxn.setVarDirect(
      this.panelSlider,
      "--string-progress-slider-fill",
      `${Math.round(rawProgress * 1000) / 10}%`,
    );

    const sliderValue = String(Math.round(rawProgress * 1000));
    if (this.panelSlider.value !== sliderValue) {
      this.panelSlider.value = sliderValue;
    }

    const displayValue = String(Math.round(progress * 100));
    if (document.activeElement !== this.panelValue) {
      this.panelValue.value = displayValue;
    }

    this.panelEasedValue.textContent = formatPercent(progress);
  }

  private findEntryByTargetLabel(targetLabel: string): ProgressEntry | null {
    for (const entry of this.entries.values()) {
      if (this.resolveOverlayTargetId(entry.object) === targetLabel) {
        return entry;
      }
    }

    return null;
  }
}

export { StringDevProgress as StringProgress };
